// src/components/reports/FlaggedGoGetCard.jsx
import React from 'react';
import { AlertTriangle, UserSquare, Calendar, Shield, Tag, Bot, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const FlaggedGoGetCard = ({ report, onReview }) => {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-red-600 bg-red-50';
      case 'medium': 
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-yellow-600 bg-yellow-50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content available';
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header with severity indicator */}
      <div className={`px-4 py-3 flex items-center justify-between ${getSeverityColor(report.severity)}`}>
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          <span className="font-medium">
            {report.severity.charAt(0).toUpperCase() + report.severity.slice(1)} Severity
          </span>
        </div>
        {report.source === 'automated' ? (
          <div className="flex items-center text-gray-600 text-sm">
            <Bot className="h-4 w-4 mr-1" />
            <span>Automated</span>
          </div>
        ) : (
          <div className="flex items-center text-gray-600 text-sm">
            <User className="h-4 w-4 mr-1" />
            <span>User Report</span>
          </div>
        )}
      </div>
      
      {/* Content preview */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg">
          {report.content?.itemName || 'Unnamed GoGet'}
        </h3>
        <p className="mt-2 text-gray-600">
          {truncateText(report.content?.itemDescription || 'No description available')}
        </p>
        
        {/* Matched terms if any */}
        {report.matchedTerms && report.matchedTerms.length > 0 && (
          <div className="mt-3">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Tag className="h-4 w-4 mr-1" />
              <span>Matched terms:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {report.matchedTerms.map((term, index) => (
                <span 
                  key={index}
                  className="inline-flex text-xs bg-red-100 text-red-800 rounded-full px-2 py-0.5"
                >
                  {term}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Matched URLs if any */}
        {report.matchedUrls && report.matchedUrls.length > 0 && (
          <div className="mt-3">
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Tag className="h-4 w-4 mr-1" />
              <span>Flagged URLs:</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {report.matchedUrls.map((url, index) => (
                <span 
                  key={index}
                  className="inline-flex text-xs bg-yellow-100 text-yellow-800 rounded-full px-2 py-0.5 truncate max-w-full"
                  title={url}
                >
                  {truncateText(url, 20)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with metadata */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center">
            <UserSquare className="h-4 w-4 mr-1" />
            <span>
              {report.reporter ? (
                <>Reported by {report.reporter}</>
              ) : (
                <>Content Filter</>
              )}
            </span>
          </div>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            <span>{formatDate(report.timestamp)}</span>
          </div>
        </div>
        <button 
          onClick={() => onReview(report._id)}
          className="mt-3 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center justify-center transition-colors"
        >
          <Shield className="h-4 w-4 mr-2" />
          Review Content
        </button>
      </div>
    </div>
  );
};

export default FlaggedGoGetCard;